// src/components/admin/tabs/pointsCenter/pointsShared.jsx
// 积分中心共享组件：时段选择器 / 双轨趋势图（纯 SVG）/ 迷你趋势。
// 视觉遵循 adminUi 工作台语言：实色面板、hairline、主题变量色、无渐变无辉光。
import React, { useState } from "react";
import { EmptyBlock } from "../../adminUi.jsx";
import {
  ACCENT,
  bucketLabel,
  COST_COLOR,
  detectPreset,
  formatCompact,
  formatMoney,
  formatPoints,
  RANGE_PRESETS,
  rangeOfPreset,
} from "./pointsShared.js";

// 时段选择器：预设 chips + 自定义日期范围 input
export const DateRangeControl = ({ from, to, onRangeChange }) => {
  const active = detectPreset(from, to);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {RANGE_PRESETS.map((preset) => {
        const isActive = active === preset.key;
        return (
          <button
            key={preset.key}
            type="button"
            onClick={() => onRangeChange(rangeOfPreset(preset.key))}
            className="h-7 px-2.5 rounded-md text-2xs font-medium border transition-colors duration-150 cursor-pointer"
            style={isActive
              ? {
                color: ACCENT,
                borderColor: "color-mix(in srgb, var(--color-warning) 40%, transparent)",
                backgroundColor: "color-mix(in srgb, var(--color-warning) 10%, transparent)",
              }
              : {
                color: "var(--color-text-muted)",
                borderColor: "var(--color-border-primary)",
                backgroundColor: "var(--color-bg-tertiary)",
              }}
          >
            {preset.label}
          </button>
        );
      })}
      <input
        type="date"
        value={from}
        max={to}
        onChange={(e) => e.target.value && onRangeChange({ from: e.target.value, to })}
        className="h-7 px-2 rounded-md text-2xs outline-none font-mono-num"
        style={{
          backgroundColor: "var(--color-bg-tertiary)",
          border: `1px solid ${active === "custom" ? "color-mix(in srgb, var(--color-warning) 40%, transparent)" : "var(--color-border-primary)"}`,
          color: "var(--color-text-primary)",
        }}
      />
      <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>~</span>
      <input
        type="date"
        value={to}
        min={from}
        onChange={(e) => e.target.value && onRangeChange({ from, to: e.target.value })}
        className="h-7 px-2 rounded-md text-2xs outline-none font-mono-num"
        style={{
          backgroundColor: "var(--color-bg-tertiary)",
          border: `1px solid ${active === "custom" ? "color-mix(in srgb, var(--color-warning) 40%, transparent)" : "var(--color-border-primary)"}`,
          color: "var(--color-text-primary)",
        }}
      />
    </div>
  );
};

// ============================
// 双轨趋势图：柱=积分（左轴），折线=¥（右轴），纯 SVG
// ============================

const CHART_W = 720;
const CHART_H = 232;
const PAD_L = 52;
const PAD_R = 52;
const PAD_T = 14;
const PAD_B = 6;

export const DualAxisChart = ({ rows, granularity }) => {
  const [hover, setHover] = useState(null);
  if (!rows.length) {
    return <EmptyBlock text="该时段内没有统计数据" hint="调整时段或粒度" />;
  }
  const innerW = CHART_W - PAD_L - PAD_R;
  const innerH = CHART_H - PAD_T - PAD_B;
  const n = rows.length;
  const slot = innerW / n;
  const barW = Math.max(2, Math.min(26, slot * 0.62));
  const maxPoints = Math.max(...rows.map((r) => Number(r.points || 0)), 1);
  const maxCost = Math.max(...rows.map((r) => Number(r.costCny || 0)), 0.01);
  const xCenter = (i) => PAD_L + slot * i + slot / 2;
  const yOfPoints = (v) => PAD_T + innerH - (Number(v || 0) / maxPoints) * innerH;
  const yOfCost = (v) => PAD_T + innerH - (Number(v || 0) / maxCost) * innerH;
  const linePoints = rows.map((r, i) => `${xCenter(i)},${yOfCost(r.costCny)}`).join(" ");
  const labelStep = Math.max(1, Math.ceil(n / 14));
  const gridFractions = [0.25, 0.5, 0.75, 1];

  return (
    <div>
      <div className="flex items-center gap-4 mb-2">
        <span className="inline-flex items-center gap-1.5 text-2xs" style={{ color: "var(--color-text-secondary)" }}>
          <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: ACCENT, opacity: 0.75 }} />
          积分消耗（左轴）
        </span>
        <span className="inline-flex items-center gap-1.5 text-2xs" style={{ color: "var(--color-text-secondary)" }}>
          <span className="w-3 border-t-2" style={{ borderColor: COST_COLOR }} />
          真实花销 ¥（右轴）
        </span>
      </div>
      <div className="relative">
        <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="w-full" style={{ height: 232, display: "block" }}>
          {gridFractions.map((f) => {
            const y = PAD_T + innerH * (1 - f);
            return (
              <g key={f}>
                <line x1={PAD_L} y1={y} x2={CHART_W - PAD_R} y2={y} stroke="var(--color-border-secondary)" strokeWidth="1" />
                <text x={PAD_L - 6} y={y + 3} textAnchor="end" fontSize="10" fill="var(--color-text-muted)" className="font-mono-num">
                  {formatCompact(maxPoints * f)}
                </text>
                <text x={CHART_W - PAD_R + 6} y={y + 3} textAnchor="start" fontSize="10" fill={COST_COLOR} opacity="0.85" className="font-mono-num">
                  {formatMoney(maxCost * f)}
                </text>
              </g>
            );
          })}
          <line x1={PAD_L} y1={PAD_T + innerH} x2={CHART_W - PAD_R} y2={PAD_T + innerH} stroke="var(--color-border-primary)" strokeWidth="1" />
          {rows.map((row, i) => {
            const barH = Math.max(Number(row.points || 0) > 0 ? 2 : 0, PAD_T + innerH - yOfPoints(row.points));
            return (
              <rect
                key={row.bucket}
                x={xCenter(i) - barW / 2}
                y={yOfPoints(row.points)}
                width={barW}
                height={barH}
                rx={2}
                fill={ACCENT}
                opacity={hover == null || hover === i ? 0.78 : 0.35}
                style={{ transition: "opacity 150ms" }}
              />
            );
          })}
          {n > 1 && (
            <polyline
              points={linePoints}
              fill="none"
              stroke={COST_COLOR}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          )}
          {rows.map((row, i) => (
            <circle
              key={`dot-${row.bucket}`}
              cx={xCenter(i)}
              cy={yOfCost(row.costCny)}
              r={hover === i ? 3.5 : 2}
              fill={COST_COLOR}
              style={{ transition: "r 150ms" }}
            />
          ))}
          {/* 悬停捕获层 */}
          {rows.map((row, i) => (
            <rect
              key={`hit-${row.bucket}`}
              x={PAD_L + slot * i}
              y={PAD_T}
              width={slot}
              height={innerH}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          ))}
        </svg>
        {hover != null && rows[hover] && (
          <div
            className="absolute top-0 z-20 rounded-lg border px-3 py-2.5 whitespace-nowrap pointer-events-none"
            style={{
              left: `${(xCenter(hover) / CHART_W) * 100}%`,
              transform: hover > n * 0.7 ? "translateX(calc(-100% - 8px))" : "translateX(8px)",
              backgroundColor: "var(--color-bg-overlay)",
              borderColor: "var(--color-border-primary)",
              boxShadow: "var(--shadow-dropdown)",
            }}
          >
            <div className="font-mono-num text-2xs mb-1.5" style={{ color: "var(--color-text-muted)" }}>
              {rows[hover].bucket}
            </div>
            <div className="font-mono-num text-sm font-semibold" style={{ color: ACCENT }}>
              {formatPoints(rows[hover].points)} 分
            </div>
            <div className="mt-1 space-y-0.5 text-2xs font-mono-num" style={{ color: "var(--color-text-secondary)" }}>
              <div style={{ color: COST_COLOR }}>{formatMoney(rows[hover].costCny)}</div>
              <div>生成 {rows[hover].count || 0} 次</div>
            </div>
          </div>
        )}
      </div>
      <div className="flex mt-1.5" style={{ paddingLeft: `${(PAD_L / CHART_W) * 100}%`, paddingRight: `${(PAD_R / CHART_W) * 100}%` }}>
        {rows.map((row, i) => (
          <div key={row.bucket} className="flex-1 text-center overflow-hidden">
            {i % labelStep === 0 && (
              <span className="font-mono-num" style={{ fontSize: 10, color: "var(--color-text-muted)" }}>
                {bucketLabel(row.bucket, granularity)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================
// 迷你趋势（polyline sparkline）
// ============================

export const Sparkline = ({ data, color = ACCENT, width = 72, height = 22 }) => {
  const values = (data || []).map((v) => Number(v || 0));
  if (values.length < 2) {
    return <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>-</span>;
  }
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const points = values
    .map((v, i) => `${((i / (values.length - 1)) * width).toFixed(1)},${(height - 2 - ((v - min) / range) * (height - 5)).toFixed(1)}`)
    .join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="shrink-0">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
        opacity="0.9"
      />
    </svg>
  );
};
